import { useEffect, useState } from "react";
import {
  clearLogs,
  exportLogsText,
  getLogs,
  shareLogFile,
  subscribeLogs,
  uploadLogsToServer,
  writeLogsToFile,
  type LogEntry,
} from "../lib/logger";
import { APP_VERSION_CODE, APP_VERSION_NAME } from "../lib/constants";
import { useSettings } from "../hooks/useSettings";
import { useAuth } from "../contexts/auth-context";
import { handleApiAuthError } from "../lib/auth-required";

type Props = {
  onClose?: () => void;
};

export function LogsPanel({ onClose }: Props) {
  const { settings } = useSettings();
  const { requireAuth } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>(() => getLogs());
  const [filter, setFilter] = useState<"all" | "error" | "network">("all");
  const [sharing, setSharing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => subscribeLogs(() => setLogs(getLogs())), []);

  const visible = logs.filter((l) => {
    if (filter === "all") return true;
    if (filter === "error") return l.level === "error" || l.level === "warn";
    return l.level === "network" || l.tag === "fetch" || l.tag === "assistant";
  });

  const share = async () => {
    if (sharing) return;
    setSharing(true);
    setStatus(null);
    try {
      const exported = await writeLogsToFile();
      await shareLogFile(exported);
      setStatus("صفحهٔ اشتراک سیستم باز شد.");
    } catch (err) {
      setStatus(
        err instanceof Error
          ? `اشتراک لاگ ناموفق: ${err.message}`
          : "اشتراک لاگ ناموفق بود."
      );
    } finally {
      setSharing(false);
    }
  };

  // Logs panel: share only — do not also auto-open share when uploading.
  const upload = async () => {
    if (uploading) return;
    requireAuth(async () => {
      setUploading(true);
      setStatus(null);
      try {
        const exported = await writeLogsToFile();
        const result = await uploadLogsToServer(settings.nevisignApiBase, {
          kind: "app-logs",
          text: exportLogsText() || exported.text,
          fileName: exported.fileName,
          versionCode: APP_VERSION_CODE,
          versionName: APP_VERSION_NAME,
        });
        if (!result.ok) {
          setStatus(result.error || "آپلود ناموفق");
        } else {
          setStatus(
            `لاگ به سرور ارسال شد${result.id ? ` (${result.id})` : ""}.`
          );
        }
      } catch (err) {
        if (handleApiAuthError(err, () => void upload())) return;
        setStatus(err instanceof Error ? err.message : "آپلود ناموفق");
      } finally {
        setUploading(false);
      }
    });
  };

  return (
    <div className="logs-panel">
      <div className="logs-toolbar">
        <select
          value={filter}
          onChange={(e) =>
            setFilter(e.target.value as "all" | "error" | "network")
          }
          aria-label="فیلتر لاگ"
        >
          <option value="all">همه</option>
          <option value="error">خطاها</option>
          <option value="network">شبکه / دستیار</option>
        </select>
        <button
          type="button"
          className="icon-btn compact"
          disabled={sharing}
          onClick={() => void share()}
        >
          {sharing ? "در حال خروجی…" : "اشتراک فایل لاگ"}
        </button>
        <button
          type="button"
          className="icon-btn compact"
          disabled={uploading}
          onClick={() => void upload()}
        >
          {uploading ? "در حال ارسال…" : "آپلود به سرور"}
        </button>
        <button
          type="button"
          className="icon-btn compact"
          onClick={() => {
            clearLogs();
            setLogs([]);
          }}
        >
          پاک‌کردن
        </button>
        {onClose && (
          <button type="button" className="icon-btn compact" onClick={onClose}>
            بستن
          </button>
        )}
      </div>
      <p className="settings-hint" dir="rtl">
        خروجی به‌صورت فایل `.txt` در صفحهٔ اشتراک اندروید قرار می‌گیرد؛ می‌توانید
        همزمان به سرور نویساین هم آپلود کنید.
      </p>
      {status && (
        <p className="settings-hint" dir="rtl" role="status">
          {status}
        </p>
      )}
      <div className="logs-list" dir="ltr">
        {visible.length === 0 && (
          <p className="settings-hint" dir="rtl">
            هنوز لاگی ثبت نشده است.
          </p>
        )}
        {visible
          .slice()
          .reverse()
          .map((e) => (
            <article key={e.id} className={`log-entry level-${e.level}`}>
              <header>
                <time>{e.ts.slice(11, 19)}</time>
                <span className="log-level">{e.level}</span>
                <span className="log-tag">{e.tag}</span>
              </header>
              <p>{e.message}</p>
              {e.detail && <pre>{e.detail.slice(0, 1200)}</pre>}
            </article>
          ))}
      </div>
    </div>
  );
}
